import React from 'react';
import './Legal.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="legal-effective">Effective Date: January 1, 2025</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="legal-content">
            <div className="legal-intro">
              <p>
                Pathway Labs, Inc. ("Pathway Labs," "we," "us," or "our") is committed to protecting
                your privacy and handling your data with integrity. This Privacy Policy explains how
                we collect, use, disclose, and safeguard your information when you visit our website
                or use our services.
              </p>
            </div>

            <div className="legal-section">
              <h2>1. Information We Collect</h2>

              <h3>Information You Provide</h3>
              <p>We collect information you voluntarily provide to us, including:</p>
              <ul>
                <li>Contact information (name, email address, phone number, organization)</li>
                <li>Account credentials if you create an account</li>
                <li>Communications you send to us (inquiries, support requests, feedback)</li>
                <li>Information submitted through forms on our website</li>
              </ul>

              <h3>Information Collected Automatically</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul>
                <li>Device information (browser type, operating system)</li>
                <li>Log data (IP address, access times, pages viewed)</li>
                <li>Cookies and similar tracking technologies (see Section 6)</li>
              </ul>

              <h3>Information from Third Parties</h3>
              <p>
                We may receive information about you from third parties, such as business partners
                or publicly available sources, only as necessary to provide our services.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our services</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send administrative information (service updates, security alerts)</li>
                <li>Communicate with you about products, services, and events (with your consent)</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Protect against fraud, unauthorized access, and other illegal activities</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>3. We Do Not Sell Your Data</h2>
              <p>
                <strong>Pathway Labs does not sell, rent, or trade your personal information to
                third parties for their marketing purposes.</strong> We believe in earning trust
                through transparency and integrity, not monetizing user data.
              </p>
              <p>
                We may share information only in the following limited circumstances:
              </p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> With trusted vendors who assist in operating
                  our business (hosting, analytics, customer support), bound by confidentiality agreements
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, court order, or
                  governmental authority
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a merger, acquisition,
                  or sale of assets, with notice to affected users
                </li>
                <li>
                  <strong>With Your Consent:</strong> When you explicitly authorize us to share
                  your information
                </li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>4. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the
                purposes for which it was collected, comply with legal obligations, resolve disputes,
                and enforce our agreements. When data is no longer needed, we securely delete or
                anonymize it.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your
                personal information against unauthorized access, alteration, disclosure, or
                destruction. These measures include:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Access controls and authentication requirements</li>
                <li>Regular security assessments and monitoring</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is
                100% secure. While we strive to protect your information, we cannot guarantee
                absolute security.
              </p>
            </div>

            <div className="legal-section">
              <h2>6. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar technologies to enhance your experience on our website.
                These may include:
              </p>
              <ul>
                <li>
                  <strong>Essential Cookies:</strong> Required for basic website functionality
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how visitors interact
                  with our website
                </li>
                <li>
                  <strong>Preference Cookies:</strong> Remember your settings and preferences
                </li>
              </ul>
              <p>
                You can control cookies through your browser settings. Disabling certain cookies
                may limit your ability to use some features of our website.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Your Rights and Choices</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul>
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict certain processing activities</li>
                <li>Data portability (receive your data in a structured format)</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us at the information provided below.
                We will respond to your request within the timeframe required by applicable law.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 13. We do not
                knowingly collect personal information from children under 13. If we become
                aware that we have collected personal information from a child under 13, we
                will take steps to delete such information promptly.
              </p>
            </div>

            <div className="legal-section">
              <h2>9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites or services. We are not
                responsible for the privacy practices of these third parties. We encourage you
                to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div className="legal-section">
              <h2>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our
                practices or applicable laws. We will notify you of material changes by posting
                the updated policy on our website with a new effective date. Your continued use
                of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="contact-block">
                <p><strong>Pathway Labs, Inc.</strong></p>
                <p>Email: privacy@pathwaylabs.io</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
